import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Cookie, X, Settings, Shield } from "lucide-react";

interface ConsentState {
  analytics: boolean;
  marketing: boolean;
  version: number;
}

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);

  useEffect(() => {
    // Check if consent has already been saved
    const savedConsent = localStorage.getItem("mt:cookie-consent");
    if (!savedConsent) {
      setIsVisible(true);
    } else {
      try {
        const parsed: ConsentState = JSON.parse(savedConsent);
        setAnalytics(parsed.analytics);
        setMarketing(parsed.marketing);
      } catch (e) {
        setIsVisible(true);
      }
    }

    // Event listener to allow reopening preferences from footer
    const handleReopen = () => {
      setIsVisible(true);
      setIsCustomizing(true);
    };

    window.addEventListener("mt:open-cookie-consent", handleReopen);
    return () => {
      window.removeEventListener("mt:open-cookie-consent", handleReopen);
    };
  }, []);

  const updateConsentMode = (analyticsGranted: boolean, marketingGranted: boolean) => {
    const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag;
    if (typeof gtag === "function") {
      gtag("consent", "update", {
        analytics_storage: analyticsGranted ? "granted" : "denied",
        ad_storage: marketingGranted ? "granted" : "denied",
        ad_user_data: marketingGranted ? "granted" : "denied",
        ad_personalization: marketingGranted ? "granted" : "denied",
      });
    }
  };

  const handleAcceptAll = () => {
    const consent: ConsentState = { analytics: true, marketing: true, version: 1 };
    localStorage.setItem("mt:cookie-consent", JSON.stringify(consent));
    setAnalytics(true);
    setMarketing(true);
    updateConsentMode(true, true);
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const consent: ConsentState = { analytics: false, marketing: false, version: 1 };
    localStorage.setItem("mt:cookie-consent", JSON.stringify(consent));
    setAnalytics(false);
    setMarketing(false);
    updateConsentMode(false, false);
    setIsVisible(false);
  };

  const handleSaveCustom = () => {
    const consent: ConsentState = { analytics, marketing, version: 1 };
    localStorage.setItem("mt:cookie-consent", JSON.stringify(consent));
    updateConsentMode(analytics, marketing);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-4 right-4 left-4 md:left-auto md:max-w-md z-50 animate-in fade-in slide-in-from-bottom-5 duration-300">
      <div className="bg-card/95 backdrop-blur-md border border-border/80 rounded-2xl p-6 shadow-elevated flex flex-col gap-4">
        {/* HEADER */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <Cookie className="h-5 w-5" />
            </div>
            <div>
              <h4 className="font-display font-bold text-foreground text-base">
                Privacidade e Cookies 🍪
              </h4>
              <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
                Gerenciador de Consentimento
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsVisible(false)}
            className="text-muted-foreground hover:text-foreground transition rounded-lg p-1 hover:bg-muted"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* CONTENT */}
        {!isCustomizing ? (
          <>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Utilizamos cookies e armazenamento local para melhorar sua experiência, analisar o
              tráfego do site e veicular anúncios personalizados com o Google AdSense.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 mt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsCustomizing(true)}
                className="w-full sm:w-auto order-3 sm:order-1 font-semibold flex items-center gap-1.5"
              >
                <Settings className="h-3.5 w-3.5" /> Personalizar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRejectAll}
                className="w-full sm:w-auto order-2 sm:order-2 font-semibold text-muted-foreground"
              >
                Recusar
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptAll}
                className="w-full sm:w-auto flex-1 order-1 sm:order-3 font-bold bg-primary hover:bg-primary/95 shadow-soft"
              >
                Aceitar todos
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Defina suas preferências de privacidade. Os cookies necessários não podem ser
              desativados pois são essenciais para o funcionamento básico da plataforma.
            </p>

            <div className="space-y-3 bg-muted/40 rounded-xl p-3.5 border border-border/40">
              {/* ESSENTIALS */}
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-0.5">
                  <div className="text-xs font-bold text-foreground">Essenciais (Obrigatório)</div>
                  <p className="text-[11px] text-muted-foreground leading-normal">
                    Configurações de tema, marca personalizada e histórico local de ferramentas.
                  </p>
                </div>
                <Switch checked disabled className="scale-90" />
              </div>

              <div className="border-t border-border/50 my-2" />

              {/* ANALYTICS */}
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-0.5">
                  <div className="text-xs font-bold text-foreground">Analíticos</div>
                  <p className="text-[11px] text-muted-foreground leading-normal">
                    Google Analytics e Ahrefs para entendermos como o site é utilizado.
                  </p>
                </div>
                <Switch checked={analytics} onCheckedChange={setAnalytics} className="scale-90" />
              </div>

              <div className="border-t border-border/50 my-2" />

              {/* MARKETING */}
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-0.5">
                  <div className="text-xs font-bold text-foreground">Publicidade e Anúncios</div>
                  <p className="text-[11px] text-muted-foreground leading-normal">
                    Google AdSense para exibição de anúncios personalizados e medição de campanhas.
                  </p>
                </div>
                <Switch checked={marketing} onCheckedChange={setMarketing} className="scale-90" />
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 mt-1">
              <button
                onClick={() => setIsCustomizing(false)}
                className="text-xs font-semibold text-muted-foreground hover:text-foreground transition underline underline-offset-2"
              >
                Voltar
              </button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRejectAll}
                  className="font-semibold"
                >
                  Recusar todos
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveCustom}
                  className="font-bold bg-primary hover:bg-primary/95"
                >
                  Salvar preferências
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* POLICY LINKS */}
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground border-t border-border/40 pt-3 mt-1 font-medium">
          <Shield className="h-3.5 w-3.5 text-primary/70" />
          <span>Veja nossa</span>
          <a href="/privacidade" className="underline hover:text-foreground transition">
            Polít. de Privacidade
          </a>
          <span>e</span>
          <a href="/cookies" className="underline hover:text-foreground transition">
            Polít. de Cookies
          </a>
        </div>
      </div>
    </div>
  );
}
